// Libs
import classNames from 'classnames/bind';
import { useCallback } from 'react';
import { Grid2, Stack, Typography } from '@mui/material';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Controller, useFormContext } from 'react-hook-form';
// Components, Layouts, Pages
import { BaseCheckbox, BaseInput, BaseSelect, BaseUploadImage } from '~/components/common';
// Others
import { IFormPackage } from '~/utils/interface/package';
import { formatInputCurrency } from '~/utils/helper/common';
import { ICategory } from '~/utils/interface/category';
import { useAppDispatch } from '~/utils/redux/hooks';
import { getListCategory } from '~/thunks/category/categoryThunk';
import { DEFAULT_LIMIT_PAGE, DEFAULT_SELECT_DYNAMIC_RESULT_FETCH_DATA } from '~/utils/constants/common';
import { IBaseFile, IBaseOption, IPayloadCallApiFn, IQueryParamBase, IResultCallApiFn } from '~/utils/interface/common';
// Styles, images, icons
import styles from './InfoPackageForm.module.scss';

type Props = {
  onDeleteImage?: (remove: boolean) => void;
};

const cx = classNames.bind(styles);

const InfoPackageForm = (props: Props) => {
  //#region Destructuring Props
  const { onDeleteImage } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/packages', 'common/base']);
  const dispatch = useAppDispatch();
  const { id: packageId } = useParams();

  const {
    control,
    watch,
    formState: { errors },
  } = useFormContext<IFormPackage>();

  const { categoryName } = watch();
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  //#endregion Implement Hook

  //#region Handle Function
  const fetchOptionsCategory = useCallback(
    async (payload: IPayloadCallApiFn): Promise<IResultCallApiFn<IBaseOption>> => {
      const { page, searchKey } = payload;
      const payloadParams: IQueryParamBase = {
        page: page,
        limit: DEFAULT_LIMIT_PAGE,
        ...(searchKey && { textSearch: searchKey }),
      };

      try {
        const resp = await dispatch(getListCategory(payloadParams)).unwrap();

        const { responses: listCategory, pagination } = resp?.data;

        const formatOptions: IBaseOption[] = handleConvertOptionCategory(listCategory);

        const hasMore: boolean = pagination?.page < pagination?.totalPages || false;

        return { optionsResp: formatOptions, hasMoreResp: hasMore };
      } catch (error) {
        return DEFAULT_SELECT_DYNAMIC_RESULT_FETCH_DATA;
      }
    },
    []
  );

  const handleConvertOptionCategory = (data: ICategory[]): IBaseOption[] => {
    const options: IBaseOption[] = data.map((item) => ({
      value: item.id,
      label: item.name,
    }));

    return options;
  };
  //#endregion Handle Function

  return (
    <Stack id='infoPackageFormComponent' className={cx('container')}>
      <Typography className={cx('header')}>{t('package_form_title_package_information')}</Typography>

      <Grid2 columnSpacing={6} rowSpacing={'15px'} container>
        <Grid2 size={4}>
          <Controller
            control={control}
            name={'title'}
            render={({ field: { onChange, value } }) => {
              return (
                <BaseInput
                  height={40}
                  label={t('package_form_title')}
                  placeholder={t('package_form_title_enter')}
                  value={value}
                  onChange={onChange}
                  errorMessage={errors?.title?.message}
                  required
                />
              );
            }}
          />
        </Grid2>

        <Grid2 size={4}>
          <Controller
            control={control}
            name={'rentalPrice'}
            render={({ field: { onChange, value } }) => {
              return (
                <BaseInput
                  height={36}
                  label={t('package_form_title_rental_price')}
                  placeholder={t('common_text_placeholder_money', { ns: 'common/base' })}
                  value={value}
                  onChange={(event) => {
                    onChange(formatInputCurrency(event.target.value));
                  }}
                  errorMessage={errors?.rentalPrice?.message}
                  required
                />
              );
            }}
          />
        </Grid2>

        <Grid2 size={4}>
          <Controller
            control={control}
            name={'categoryId'}
            render={({ field: { onChange, value } }) => {
              return (
                <BaseSelect
                  isSearch
                  type='loadMore'
                  gap={10}
                  height={40}
                  itemNameDefault={categoryName}
                  styleType='contained'
                  label={t('package_form_title_category')}
                  callApiFn={fetchOptionsCategory}
                  value={value}
                  onChange={(data) => onChange(data?.value)}
                  errorMessage={errors?.categoryId?.message}
                />
              );
            }}
          />
        </Grid2>

        <Grid2 size={4}>
          <Controller
            control={control}
            name={'description'}
            render={({ field: { onChange, value } }) => {
              return (
                <BaseInput
                  height={40}
                  label={t('package_form_title_short_description')}
                  placeholder={t('package_form_title_enter_description')}
                  value={value}
                  onChange={onChange}
                  errorMessage={errors?.description?.message}
                />
              );
            }}
          />
        </Grid2>

        <Grid2 size={4}>
          <Controller
            control={control}
            name={'note'}
            render={({ field: { onChange, value } }) => {
              return (
                <BaseInput
                  height={40}
                  label={t('package_form_title_note')}
                  placeholder={t('package_form_title_enter_note')}
                  value={value}
                  onChange={onChange}
                  errorMessage={errors?.note?.message}
                />
              );
            }}
          />
        </Grid2>

        <Grid2 size={1.5}>
          <Stack direction={'row'} height={'100%'} pt={8}>
            <Controller
              name='isTaxable'
              control={control}
              render={({ field: { value, onChange } }) => (
                <BaseCheckbox
                  label={t('package_form_title_taxable')}
                  checked={value}
                  onChange={(value) => onChange(value)}
                />
              )}
            />
          </Stack>
        </Grid2>

        {/*<Grid2 size={2.5}>*/}
        {/*  <Stack direction={'row'} height={'100%'} pt={8}>*/}
        {/*    <Controller*/}
        {/*      name='isShowItem'*/}
        {/*      control={control}*/}
        {/*      render={({ field: { value, onChange } }) => (*/}
        {/*        <BaseCheckbox*/}
        {/*          label={t('package_form_title_show_items')}*/}
        {/*          checked={value}*/}
        {/*          onChange={(value) => onChange(value)}*/}
        {/*        />*/}
        {/*      )}*/}
        {/*    />*/}
        {/*  </Stack>*/}
        {/*</Grid2>*/}

        <Grid2 size={12}>
          <Controller
            control={control}
            name={'packageImage'}
            render={({ field: { onChange, value } }) => {
              return (
                <BaseUploadImage
                  label={t('package_form_title_image')}
                  {...(packageId && !value?.file && { uploadedFiles: [value || {}] })}
                  onChange={(files: IBaseFile[]) => onChange(files?.[0])}
                  errorMessage={errors.packageImage?.message}
                  isMultiple={false}
                  isButton={false}
                  height={130}
                  onRemove={() => onDeleteImage?.(true)}
                />
              );
            }}
          />
        </Grid2>
      </Grid2>
    </Stack>
  );
};

export default InfoPackageForm;
